import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Stack, Tooltip, Chip, Grid, Paper, Avatar,
  TextField, Dialog, DialogContent, DialogActions,
  Snackbar, Alert, InputAdornment, Switch, CircularProgress,
} from '@mui/material';
import {
  RefreshOutlined, CheckCircleOutlined, ErrorOutlined, SearchOutlined,
  CloseOutlined, SyncOutlined, LinkOffOutlined,
  TagOutlined, GroupsOutlined, CampaignOutlined, ForumOutlined,
  BusinessCenterOutlined, PaymentsOutlined, SpaOutlined, AccountBalanceOutlined,
  OpenInNewOutlined,
  PersonSearchOutlined, HubOutlined, ReceiptOutlined, PointOfSaleOutlined,
  InventoryOutlined, CloudOutlined, CalculateOutlined, CloudUploadOutlined,
  InboxOutlined, CloudSyncOutlined, RocketLaunchOutlined, ChatOutlined,
  SendOutlined, LockOutlined,
} from '@mui/icons-material';
import { connectionsApi, hrisApi, type ConnectionResponse, type HrisConnectionResponse } from '../../api/integrations';
import HrisConnectDialog from './HrisConnectDialog';
import QuickBooksExportDialog from './QuickBooksExportDialog';
import { currentApi } from '../../api/subscription';
import { openSupportCase } from '../../components/support/SupportCaseDialog';
import { useTranslation } from 'react-i18next';
import { activeLocale } from '../../utils/datetime';
import {
  integrationCatalog, CATEGORIES,
  type ProviderDefinition, type ProviderCategory,
} from './integrationCatalog';

type Toast = { message: string; severity: 'success' | 'error' | 'info' } | null;
type Tier = 'free' | 'starter' | 'professional' | 'enterprise';

// Per-event mute UI vocabulary. Order matters — admins read top-down.
const EVENT_LABELS: { key: string; label: string; i18nKey?: string; defaultOn: boolean }[] = [
  { key: 'clock_in_late',                   label: 'Late clock-in alerts', i18nKey: 'integrations:connectors.eventLabels.clockInLate',         defaultOn: true },
  { key: 'leave_request_submitted',         label: 'Leave request submitted', i18nKey: 'integrations:connectors.eventLabels.leaveSubmitted',      defaultOn: true },
  { key: 'leave_request_approved',          label: 'Leave approved', i18nKey: 'integrations:connectors.eventLabels.leaveApproved',               defaultOn: true },
  { key: 'leave_request_rejected',          label: 'Leave rejected', i18nKey: 'integrations:connectors.eventLabels.leaveRejected',               defaultOn: true },
  { key: 'process_request_submitted',       label: 'Process request submitted', i18nKey: 'integrations:connectors.eventLabels.processSubmitted',    defaultOn: true },
  { key: 'process_request_status_changed',  label: 'Process request status change', i18nKey: 'integrations:connectors.eventLabels.processStatusChange', defaultOn: true },
  { key: 'shift_assigned',                  label: 'Shift assigned', i18nKey: 'integrations:connectors.eventLabels.shiftAssigned',               defaultOn: false },
];

const TIER_RANK: Record<string, number> = {
  free: 0, starter: 1, professional: 2, enterprise: 3,
};

function tierAtLeast(actual: string | null | undefined, required: Tier): boolean {
  return (TIER_RANK[actual ?? ''] ?? -1) >= TIER_RANK[required];
}

// Icon map: catalog icon string → MUI component
const ICON_MAP: Record<string, React.ReactElement> = {
  Tag: <TagOutlined />, Groups: <GroupsOutlined />, Campaign: <CampaignOutlined />, Forum: <ForumOutlined />,
  BusinessCenter: <BusinessCenterOutlined />, Payments: <PaymentsOutlined />, Spa: <SpaOutlined />,
  AccountBalance: <AccountBalanceOutlined />, PersonSearch: <PersonSearchOutlined />, Hub: <HubOutlined />,
  Receipt: <ReceiptOutlined />, PointOfSale: <PointOfSaleOutlined />, Inventory: <InventoryOutlined />,
  Cloud: <CloudOutlined />, Calculate: <CalculateOutlined />, CloudUpload: <CloudUploadOutlined />,
  Inbox: <InboxOutlined />, CloudSync: <CloudSyncOutlined />, RocketLaunch: <RocketLaunchOutlined />, Chat: <ChatOutlined />,
};

const Connectors: React.FC = () => {
  const { t } = useTranslation();
  const [connections, setConnections] = useState<ConnectionResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | ProviderCategory>('all');
  const [search, setSearch] = useState('');
  const [dialogProvider, setDialogProvider] = useState<ProviderDefinition | null>(null);
  const [dialogConnection, setDialogConnection] = useState<ConnectionResponse | null>(null);
  // HRIS (hr_payroll) connectors use a separate dialog + endpoints.
  const [hrisConnections, setHrisConnections] = useState<HrisConnectionResponse[]>([]);
  const [hrisDialogProvider, setHrisDialogProvider] = useState<ProviderDefinition | null>(null);
  // ADR-041: QuickBooks export uses a dedicated OAuth + export dialog.
  const [quickbooksDialogProvider, setQuickbooksDialogProvider] = useState<ProviderDefinition | null>(null);
  const [configValues, setConfigValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<Toast>(null);

  // Day 4: per-event mute state + subscription tier (gate the toggles).
  const [orgTier, setOrgTier] = useState<string | null>(null);
  const [subs, setSubs] = useState<Record<string, boolean>>({});
  const [savingSubs, setSavingSubs] = useState<string | null>(null); // event key being toggled
  const [sendingTest, setSendingTest] = useState(false);

  const providerDisplayName = useCallback((providerKey: string) => {
    const provider = integrationCatalog.find((p) => p.key === providerKey);
    return provider ? t(provider.nameKey) : providerKey;
  }, [t]);

  const fetchConnections = useCallback(async () => {
    try { setConnections(await connectionsApi.list()); } catch (e) { console.warn('Failed to load connections', e); } finally { setLoading(false); }
  }, []);

  const fetchHrisConnections = useCallback(async () => {
    try { setHrisConnections((await hrisApi.connections()).items); } catch (e) { console.warn('Failed to load HRIS connections', e); }
  }, []);

  useEffect(() => { fetchConnections(); }, [fetchConnections]);
  useEffect(() => { fetchHrisConnections(); }, [fetchHrisConnections]);

  // Fetch org's current tier once on mount; used to gate the per-event
  // mute UI (Pro+). Failure → leave null → toggles render disabled
  // with the upgrade hint regardless.
  useEffect(() => {
    let alive = true;
    currentApi.get().then((sub) => {
      if (!alive) return;
      const tier = (sub as { plan?: { tier?: string } | null } | null)?.plan?.tier ?? null;
      setOrgTier(tier);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // P0.1 Path A3: surface OAuth callback result. Backend redirects
  // /v1/integrations/{provider}/oauth/callback back to /admin/integrations
  // with ?connected=slack on success or ?integration_error=<code> on
  // failure. Show a Snackbar then strip the query so a refresh
  // doesn't re-fire it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const errCode = params.get('integration_error');
    if (connected) {
      setToast({
        message: t('integrations:connectors.connectedTo', { name: providerDisplayName(connected) }),
        severity: 'success',
      });
    } else if (errCode) {
      setToast({
        message: errCode === 'license_required'
          ? t('integrations:connectors.licenseRequired')
          : t('integrations:connectors.connectionFailed', { error: errCode.replace(/_/g, ' ') }),
        severity: 'error',
      });
    }
    if (connected || errCode) {
      params.delete('connected');
      params.delete('integration_error');
      const qs = params.toString();
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (qs ? `?${qs}` : ''),
      );
    }
  }, [providerDisplayName, t]);

  // Microsoft Teams Phase 4 (alembic 043): multi-channel support.
  // connMap holds the 'primary' connection per provider (back-compat
  // with single-channel admin flows). connByProvider returns the full
  // list keyed by provider so we can show "+ N more channels" badges
  // for orgs that programmatically created additional labelled
  // connections via the API. Phase 4.B.2 adds a full multi-channel
  // CRUD UI.
  const connByProvider = new Map<string, ConnectionResponse[]>();
  for (const c of connections) {
    const list = connByProvider.get(c.provider) ?? [];
    list.push(c);
    connByProvider.set(c.provider, list);
  }
  const connMap = new Map<string, ConnectionResponse | undefined>();
  // First pass: prefer the 'primary' connection.
  connections
    .filter((c) => (c.connection_label ?? 'primary') === 'primary')
    .forEach((c) => connMap.set(c.provider, c));
  // Fallback pass: if NO 'primary' row exists (rare, programmatic-only),
  // use the first available so the card still renders.
  connByProvider.forEach((list, provider) => {
    if (!connMap.has(provider) && list.length > 0) {
      connMap.set(provider, list[0]);
    }
  });

  const filtered = integrationCatalog.filter(p => {
    if (activeTab !== 'all' && p.category !== activeTab) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }).sort((a, b) =>
    // Available (connectable) first, then Coming Soon; within each group,
    // popular first. Fixes popular-but-coming-soon items (WhatsApp/QuickBooks/
    // Google Drive) floating above available connectors (owner 2026-07-02).
    (a.comingSoon ? 1 : 0) - (b.comingSoon ? 1 : 0) ||
    (b.popular ? 1 : 0) - (a.popular ? 1 : 0)
  );

  const openDialog = (provider: ProviderDefinition) => {
    const existing = connMap.get(provider.key);
    setDialogProvider(provider);
    setDialogConnection(existing || null);
    const initial: Record<string, string> = {};
    provider.configFields.forEach(f => {
      initial[f.key] = (existing?.config as Record<string, string>)?.[f.key] || '';
    });
    setConfigValues(initial);
    // Day 4: seed event_subscriptions from the connection's stored
    // config; absent map (legacy connection) → all-on default.
    const fromCfg = (existing?.config as { event_subscriptions?: Record<string, boolean> } | null)?.event_subscriptions;
    if (fromCfg) {
      setSubs({ ...fromCfg });
    } else {
      const defaults: Record<string, boolean> = {};
      EVENT_LABELS.forEach(e => { defaults[e.key] = e.defaultOn; });
      setSubs(defaults);
    }
  };

  const closeDialog = () => { setDialogProvider(null); setDialogConnection(null); };

  const handleConnect = async () => {
    if (!dialogProvider) return;
    // P0.1 Path A3: OAuth providers (Slack) hand off to the
    // backend /oauth/start endpoint instead of writing config inline.
    if (dialogProvider.oauthFlow) {
      // 2026-05-25 Bug 5 fix — client-side tier gate.
      // Pre-fix: clicking Connect when tier was insufficient ended up
      // on dashboard (relative-URL navigation hit qutime.com SPA
      // fallback, no upgrade prompt). Now we surface the Upgrade
      // dialog before any cross-origin call.
      const minTier = dialogProvider.minTier ?? 'free';
      if (!tierAtLeast(orgTier, minTier as Tier)) {
        setToast({
          message: t('integrations:connectors.tierRequired', { name: t(dialogProvider.nameKey), tier: minTier.charAt(0).toUpperCase() + minTier.slice(1) }),
          severity: 'info',
        });
        // Trigger the TopNav-level Upgrade dialog (subscribes to
        // 'svdy:upgrade:open' as of 2026-05-25 fix).
        window.dispatchEvent(new CustomEvent('svdy:upgrade:open'));
        return;
      }

      // Tier OK — fetch the auth URL via XHR (carries Bearer token)
      // and navigate. Pre-fix `window.location = '/v1/...'` was a
      // relative-URL navigation that landed on qutime.com SPA's
      // not-found / dashboard fallback because qutime.com has no
      // /v1/* CloudFront proxy. New flow: backend supports
      // ?response=json mode, returns {auth_url}; we navigate from
      // there.
      setSaving(true);
      try {
        const returnUrl = '/admin/integrations';
        const resp = await connectionsApi.oauthStart(
          dialogProvider.key,
          returnUrl,
        );
        if (resp?.auth_url) {
          window.location.href = resp.auth_url;
          return;
        }
        setToast({
          message: t('common:feedback.actionFailed'),
          severity: 'error',
        });
      } catch (e: unknown) {
        // Backend 402 license_required → frontend client tier check
        // already short-circuits before hitting the network, but
        // backend re-checks for race / mid-flow downgrade. Surface
        // upgrade hint either way.
        const err = e as { status?: number; message?: string };
        if (err?.status === 402) {
          setToast({
            message: t('integrations:connectors.planRequired'),
            severity: 'info',
          });
          window.dispatchEvent(new CustomEvent('svdy:upgrade:open'));
        } else {
          setToast({
            message: err.message || t('integrations:connectors.connectionFailedGeneric'),
            severity: 'error',
          });
        }
      } finally {
        setSaving(false);
      }
      return;
    }
    setSaving(true);
    try {
      await connectionsApi.connect(dialogProvider.key, {
        provider: dialogProvider.key, category: dialogProvider.category, config: configValues,
      });
      setToast({ message: t('integrations:connectors.connectedTo', { name: t(dialogProvider.nameKey) }), severity: 'success' });
      closeDialog();
      fetchConnections();
    } catch (e: unknown) {
      setToast({ message: (e as Error).message || t('integrations:connectors.connectionFailedGeneric'), severity: 'error' });
    } finally { setSaving(false); }
  };

  const handleDisconnect = async () => {
    if (!dialogProvider) return;
    setSaving(true);
    try {
      await connectionsApi.disconnect(dialogProvider.key);
      setToast({ message: t('integrations:connectors.disconnectedFrom', { name: t(dialogProvider.nameKey) }), severity: 'success' });
      closeDialog();
      fetchConnections();
    } catch (e: unknown) {
      setToast({ message: (e as Error).message || t('integrations:connectors.disconnectFailed'), severity: 'error' });
    } finally { setSaving(false); }
  };

  const handleSync = async () => {
    if (!dialogProvider) return;
    setSaving(true);
    try {
      await connectionsApi.sync(dialogProvider.key);
      setToast({ message: t('integrations:connectors.syncTriggered', { name: t(dialogProvider.nameKey) }), severity: 'success' });
      fetchConnections();
    } catch (e: unknown) {
      setToast({ message: (e as Error).message || t('integrations:connectors.syncFailed'), severity: 'error' });
    } finally { setSaving(false); }
  };

  // Day 4: per-event mute toggle. Optimistic UI + revert on failure.
  // Pro+ gate enforced server-side; lower tiers get 400 → revert.
  const handleToggleEvent = async (eventKey: string) => {
    if (!dialogProvider || savingSubs) return;
    const next = !subs[eventKey];
    const prev = subs[eventKey];
    setSubs(s => ({ ...s, [eventKey]: next }));
    setSavingSubs(eventKey);
    try {
      const updated = await connectionsApi.updateEventSubscriptions(
        dialogProvider.key, { [eventKey]: next },
      );
      const fresh = (updated.config as { event_subscriptions?: Record<string, boolean> } | null)?.event_subscriptions;
      if (fresh) setSubs({ ...fresh });
      // Refresh card-grid status / last_sync_at
      fetchConnections();
    } catch (e: unknown) {
      setSubs(s => ({ ...s, [eventKey]: prev }));
      const msg = (e as Error).message || t('common:feedback.actionFailed');
      setToast({
        message: msg.includes('Professional')
          ? t('integrations:connectors.eventSubTierRequired')
          : msg,
        severity: 'error',
      });
    } finally {
      setSavingSubs(null);
    }
  };

  // Day 4: fire a synthetic 'svdy test message' through the provider.
  const handleSendTest = async () => {
    if (!dialogProvider || sendingTest) return;
    setSendingTest(true);
    try {
      const r = await connectionsApi.sendTest(dialogProvider.key);
      setToast({
        message: r.sent
          ? t('integrations:connectors.testSent', { name: t(dialogProvider.nameKey) })
          : t('integrations:connectors.testFailed', { detail: r.detail ?? t('integrations:connectors.unknownError') }),
        severity: r.sent ? 'success' : 'error',
      });
      fetchConnections();
    } catch (e: unknown) {
      setToast({ message: (e as Error).message || t('integrations:connectors.testFailedGeneric'), severity: 'error' });
    } finally {
      setSendingTest(false);
    }
  };

  const isMessagingProvider = (p: ProviderDefinition | null): boolean =>
    !!p && p.category === 'messaging';

  const statusChip = (provider: string) => {
    const conn = connMap.get(provider);
    if (!conn) return <Chip label={t('integrations:connectors.notConnected')} size="small" variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />;
    if (conn.status === 'active') return <Chip icon={<CheckCircleOutlined sx={{ fontSize: 12 }} />} label={t('integrations:connectors.connected')} size="small" color="success" variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />;
    return <Chip icon={<ErrorOutlined sx={{ fontSize: 12 }} />} label={t('integrations:connectors.error')} size="small" color="error" variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />;
  };

  const dataFlowLabel = (flow: string) => {
    if (flow === 'bidirectional') return t('integrations:connectors.bidirectional');
    if (flow === 'one_way_push') return t('integrations:connectors.push');
    return t('integrations:connectors.pull');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      {/* Header */}
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5, mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('integrations:connectors.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('integrations:connectors.subtitleDetail')}</Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}><IconButton size="small" onClick={fetchConnections} sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}><RefreshOutlined fontSize="small" /></IconButton></Tooltip>
          {/* HANDOFF_2026-05-25 Bug 26 — top-right View docs button.
              Routes to the integrations index on svdy.com (the
              SMB-friendly card directory; per-vendor cards link
              into the dedicated guide pages). */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<OpenInNewOutlined />}
            component="a"
            href="https://svdy.com/integrations"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem', color: 'text.primary', borderColor: 'rgba(0,0,0,0.2)', bgcolor: '#ffffff', '&:hover': { borderColor: 'rgba(0,0,0,0.35)', bgcolor: '#ffffff' } }}
          >
            {t('integrations:connectors.viewDocs')}
          </Button>
        </Stack>
      </Box>

      {/* Tabs + Search */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, width: '100%', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'inline-flex', gap: 0.5, p: 0.375, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
          <Button size="small" onClick={() => setActiveTab('all')}
            sx={{ px: 2.5, py: 0.5, borderRadius: 1, textTransform: 'none', fontSize: '0.8rem', fontWeight: activeTab === 'all' ? 600 : 500, bgcolor: activeTab === 'all' ? 'primary.main' : 'transparent', color: activeTab === 'all' ? 'primary.contrastText' : 'text.secondary', '&:hover': { bgcolor: activeTab === 'all' ? 'primary.main' : 'action.hover' } }}>
            {t('integrations:connectors.allCount', { count: integrationCatalog.length })}
          </Button>
          {CATEGORIES.map(cat => (
            <Button key={cat.key} size="small" onClick={() => setActiveTab(cat.key)}
              sx={{ px: 2.5, py: 0.5, borderRadius: 1, textTransform: 'none', fontSize: '0.8rem', fontWeight: activeTab === cat.key ? 600 : 500, bgcolor: activeTab === cat.key ? 'primary.main' : 'transparent', color: activeTab === cat.key ? 'primary.contrastText' : 'text.secondary', '&:hover': { bgcolor: activeTab === cat.key ? 'primary.main' : 'action.hover' } }}>
              {t(cat.i18nKey)} {integrationCatalog.filter(p => p.category === cat.key).length}
            </Button>
          ))}
        </Box>
        <TextField size="small" placeholder={t('integrations:connectors.searchPlaceholder', { defaultValue: 'Search connectors...' })} value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
          sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: 1, height: 34 } }} />
      </Stack>

      {/* Card Grid */}
      {loading ? (
        <Typography variant="body2" color="text.secondary">{t('common:status.loading')}</Typography>
      ) : filtered.length === 0 ? (
        <Paper sx={{ p: 4, width: '100%', textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <Typography variant="body2" color="text.secondary">{t('integrations:connectors.noConnectors')}</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filtered.map(p => {
            const conn = connMap.get(p.key);
            const providerConnections = connByProvider.get(p.key) ?? [];
            const extraChannelCount = Math.max(providerConnections.length - 1, 0);
            const extraChannelLabels = providerConnections
              .map((c) => c.connection_label ?? 'primary')
              .filter((label) => label !== 'primary');
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={p.key}>
                <Paper onClick={() => {
                    if (p.comingSoon) return;
                    if (p.key === 'quickbooks') setQuickbooksDialogProvider(p);
                    else if (p.category === 'hr_payroll') setHrisDialogProvider(p);
                    else openDialog(p);
                  }} sx={{
                  p: 2, borderRadius: 2, cursor: p.comingSoon ? 'default' : 'pointer', height: '100%', display: 'flex', flexDirection: 'column',
                  border: '1px solid', borderColor: conn?.status === 'error' ? 'error.main' : (p.comingSoon ? 'divider' : 'rgba(25,118,210,0.35)'),
                  bgcolor: p.comingSoon ? 'grey.50' : '#fff',
                  opacity: p.comingSoon ? 0.55 : 1,
                  transition: 'border-color 120ms ease', '&:hover': p.comingSoon ? {} : { borderColor: 'primary.main' },
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Avatar variant="rounded" sx={{ width: 36, height: 36, bgcolor: conn ? 'primary.50' : 'grey.100', color: conn ? 'primary.main' : 'text.secondary' }}>
                      {ICON_MAP[p.icon] || <HubOutlined />}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.2 }}>{t(p.nameKey)}</Typography>
                      {statusChip(p.key)}
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, flex: 1, fontSize: '0.7rem' }}>{t(p.descriptionKey)}</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {p.popular && !p.comingSoon && <Chip label={t('integrations:connectors.popular')} size="small" color="warning" sx={{ borderRadius: 1, height: 18, fontSize: '0.6rem', fontWeight: 600 }} />}
                    {p.comingSoon && <Chip label={t('integrations:connectors.comingSoon')} size="small" color="info" sx={{ borderRadius: 1, height: 18, fontSize: '0.6rem', fontWeight: 600 }} />}
                    <Chip label={dataFlowLabel(p.dataFlow)} size="small" variant="outlined" sx={{ borderRadius: 1, height: 18, fontSize: '0.6rem' }} />
                    {/* Microsoft Teams Phase 4 — multi-channel awareness.
                        Show a "+N channels" chip when the org has more than
                        one labelled connection for this provider. Hover
                        reveals the list. Phase 4.B.2 adds add/edit/delete
                        for non-primary channels via UI; for now multi-
                        channel CRUD is API-only. */}
                    {extraChannelCount > 0 && (
                      <Chip
                        label={t('integrations:connectors.moreChannels', { count: extraChannelCount })}
                        size="small"
                        color="primary"
                        variant="outlined"
                        title={extraChannelLabels.join(', ')}
                        sx={{ borderRadius: 1, height: 18, fontSize: '0.6rem', fontWeight: 600 }}
                      />
                    )}
                    {conn?.last_sync_at && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {t('integrations:connectors.syncedAt', {
                          time: new Date(conn.last_sync_at).toLocaleString(activeLocale(), { hour: '2-digit', minute: '2-digit' }),
                        })}
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        {t('integrations:connectors.dontSeeSystem')}{' '}
        <Typography
          component="span"
          variant="caption"
          sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => {
            // HANDOFF_2026-05-25 Bug 24 — reuse the shared SupportCaseDialog
            // (already used by attendance reports' 'Tell us' flow + system
            // support 'Send Feedback'). Owner: '设计要跟 attendance reports
            // 中 tell us 的弹窗一样！专业一点吧，统一的用户 UX 使用体验,
            // 不要割裂'.
            openSupportCase({
              category: 'integration',
              priority: 'low',
              subject: 'New connector request: ',
              description:
                'Which system / vendor would you like to connect SVDY to?\n\n' +
                'What data would you like to sync (worker import, attendance push, payroll export, etc.)?\n\n' +
                'Roughly how many users would benefit from this integration?',
            });
          }}
        >
          {t('integrations:connectors.requestIntegration')}
        </Typography>
      </Typography>

      {/* Connect / Configure Dialog */}
      <Dialog open={!!dialogProvider} onClose={closeDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
        {dialogProvider && (
          <>
            {/* Colored header banner */}
            <Box sx={{
              p: 2.5,
              background: dialogConnection
                ? 'linear-gradient(135deg, #2e7d32, #2e7d32dd)'
                : 'linear-gradient(135deg, #1565c0, #1565c0dd)',
              color: '#fff',
              position: 'relative',
            }}>
              <IconButton aria-label={t('common:actions.close')} size="small" onClick={closeDialog} sx={{ position: 'absolute', top: 8, right: 8, color: 'inherit', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                <CloseOutlined fontSize="small" />
              </IconButton>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar variant="rounded" sx={{ width: 48, height: 48, bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit', '& svg': { fontSize: 28 } }}>
                  {ICON_MAP[dialogProvider.icon] || <HubOutlined />}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>{t(dialogProvider.nameKey)}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Chip label={(() => { const c = CATEGORIES.find(c => c.key === dialogProvider.category); return c ? t(c.i18nKey) : ''; })()} size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit', fontWeight: 600, height: 22, fontSize: '0.7rem' }} />
                    <Chip label={dataFlowLabel(dialogProvider.dataFlow)} size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'inherit', fontWeight: 500, height: 22, fontSize: '0.7rem' }} />
                    {dialogConnection && (
                      <Chip icon={<CheckCircleOutlined sx={{ fontSize: 12, color: 'inherit' }} />} label={t('integrations:connectors.connected')} size="small"
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit', fontWeight: 600, height: 22, fontSize: '0.7rem' }} />
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Box>

            <DialogContent sx={{ py: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t(dialogProvider.descriptionKey)}</Typography>

              {/* HANDOFF_2026-05-25 Bug 23b — View setup guide CTA pointing
                  to the public-facing svdy.com integration docs. SMB-novice
                  admins get the full walkthrough (value props + step-by-step
                  install) before clicking Connect. */}
              {dialogProvider.guideUrl && (
                <Box
                  sx={{
                    mb: 2.5,
                    p: 1.25,
                    border: '1px dashed',
                    borderColor: 'primary.light',
                    borderRadius: 1,
                    bgcolor: 'primary.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.primary', flex: 1 }}>
                    {t('integrations:connectors.setupGuideHint')}
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    component="a"
                    href={dialogProvider.guideUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {t('integrations:connectors.viewSetupGuide')}
                  </Button>
                </Box>
              )}

              {/* Connection info card (when connected) */}
              {dialogConnection && (
                <Box sx={{ mb: 2.5, display: 'flex', gap: 1.5 }}>
                  <Box sx={{ flex: 1, p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary">{t('integrations:connectors.statusLabel')}</Typography>
                    <Typography variant="body2" fontWeight={600} color="success.main" sx={{ mt: 0.25 }}>{dialogConnection.status}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary">{t('integrations:connectors.lastSync')}</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
                      {dialogConnection.last_sync_at ? new Date(dialogConnection.last_sync_at).toLocaleString(activeLocale()) : t('integrations:connectors.never')}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Day 4: Per-event mute panel + Send Test (IM providers only,
                  when connected). Pro+ tier gate; lower tiers see toggles
                  disabled with the upgrade hint.  */}
              {dialogConnection && isMessagingProvider(dialogProvider) && (
                <Box sx={{ mb: 2.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      {t('integrations:connectors.notificationEvents')}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={sendingTest ? <CircularProgress size={12} /> : <SendOutlined sx={{ fontSize: 14 }} />}
                      disabled={sendingTest}
                      onClick={handleSendTest}
                      sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      {t('integrations:connectors.sendTest')}
                    </Button>
                  </Stack>
                  {!tierAtLeast(orgTier, 'professional') && (
                    <Alert
                      severity="info"
                      variant="outlined"
                      icon={<LockOutlined sx={{ fontSize: 16 }} />}
                      sx={{ mb: 1, fontSize: '0.75rem', py: 0.5 }}
                    >
                      {t('integrations:connectors.perEventMuteHint')}
                    </Alert>
                  )}
                  <Box sx={{
                    border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                    overflow: 'hidden',
                  }}>
                    {EVENT_LABELS.map((evt, idx) => (
                      <Box key={evt.key} sx={{
                        display: 'flex', alignItems: 'center', px: 1.5, py: 0.75,
                        borderTop: idx === 0 ? 'none' : '1px solid',
                        borderColor: 'divider',
                      }}>
                        <Typography variant="body2" sx={{ flex: 1, fontSize: '0.8rem' }}>
                          {evt.i18nKey ? t(evt.i18nKey) : evt.label}
                        </Typography>
                        {savingSubs === evt.key ? (
                          <CircularProgress size={14} sx={{ mr: 1 }} />
                        ) : (
                          <Switch
                            size="small"
                            checked={!!subs[evt.key]}
                            disabled={!tierAtLeast(orgTier, 'professional') || !!savingSubs}
                            onChange={() => handleToggleEvent(evt.key)}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Config form (hidden once connected for IM providers — the per-event
                  mute panel above replaces re-entry of the webhook URL). */}
              {!(dialogConnection && isMessagingProvider(dialogProvider)) && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {dialogProvider.oauthFlow ? t('integrations:connectors.install') : t('integrations:connectors.configuration')}
                  </Typography>
                  {dialogProvider.oauthFlow ? (
                    <Box sx={{ p: 2, borderRadius: 1.5, border: '1px dashed', borderColor: 'divider', bgcolor: 'grey.50' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t('integrations:connectors.oauthHint', { name: t(dialogProvider.nameKey) })}
                      </Typography>
                      {dialogProvider.minTier && (
                        <Typography variant="caption" color="text.secondary">
                          {t('integrations:connectors.requiredTier', { tier: dialogProvider.minTier })}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Stack spacing={2}>
                      {dialogProvider.configFields.map(field => (
                        <TextField key={field.key} label={t(field.i18nKey)} placeholder={field.placeholder}
                          type={field.type === 'password' ? 'password' : 'text'}
                          value={configValues[field.key] || ''} size="small" fullWidth required={field.required}
                          onChange={e => setConfigValues(prev => ({ ...prev, [field.key]: e.target.value }))} />
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions sx={{ py: 2 }}>
              {dialogConnection ? (
                <>
                  <Button variant="outlined" size="small" startIcon={<SyncOutlined />} onClick={handleSync} disabled={saving}
                    sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.8rem' }}>{t('integrations:connectors.syncNow', { defaultValue: 'Sync Now' })}</Button>
                  <Box sx={{ flex: 1 }} />
                  <Button variant="outlined" size="small" color="error" startIcon={<LinkOffOutlined />} onClick={handleDisconnect} disabled={saving}
                    sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.8rem' }}>{t('integrations:connectors.disconnect')}</Button>
                </>
              ) : (
                <>
                  <Button onClick={closeDialog} size="small" sx={{ borderRadius: 1, textTransform: 'none' }}>{t('common:actions.cancel')}</Button>
                  <Button variant="contained" size="small" onClick={handleConnect} disabled={saving}
                    sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>
                    {saving ? t('integrations:connectors.connecting') : t('integrations:connectors.connect')}
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* HRIS connect dialog (Workday / BambooHR) — separate endpoints + Test/Sync actions */}
      <HrisConnectDialog
        provider={hrisDialogProvider}
        connection={hrisDialogProvider ? (hrisConnections.find((c) => c.provider === hrisDialogProvider.key) ?? null) : null}
        onClose={() => setHrisDialogProvider(null)}
        onChanged={fetchHrisConnections}
      />

      {/* QuickBooks export dialog (ADR-041) — OAuth connect + date-range export */}
      <QuickBooksExportDialog
        provider={quickbooksDialogProvider}
        onClose={() => setQuickbooksDialogProvider(null)}
        onChanged={fetchConnections}
      />

      {/* Toast */}
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast?.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Connectors;
